import { Injectable } from "@angular/core"
import { Title } from "@angular/platform-browser"
import { Resource } from "../models/resource/resource"
import { LearningObject } from "../models/learningObject.model"
import { Subject } from "rxjs"
import { GTMData, PageData, TagData } from "../utils/types"
import { VideoPlayerInfo } from "../utils/enums"
import { GtmDimensions, GtmEvent } from "../utils/enums"
import Utils from "../utils/utils"

declare var window: any

@Injectable()
export class GTMService {
    private lastPageTitle = "not set"
    private lastPageURL = "not set"

    private subject = new Subject<PageData>()

    constructor(private titleService: Title) {}

    pushEvent(eventName) {
        this.pushToDataLayer({ event: eventName })
    }

    pushPageView() {
        const currentTitle = this.titleService.getTitle()
        const currentURL = window.location.href
        this.pushToDataLayer({
            [GtmDimensions.LastPageTitle]: this.lastPageTitle,
            [GtmDimensions.LastPageUrl]: this.lastPageURL,
        })
        this.pushToDataLayer({ event: "LMPageView" })
        this.lastPageTitle = currentTitle
        this.lastPageURL = currentURL
    }

    public setVideoData(data) {
        window.dataLayer.push({ [GtmDimensions.VideoData]: data })
    }

    resetDataLayer() {
        this.pushToDataLayer({
            [GtmDimensions.ResourceSubject]: null,
            [GtmDimensions.ContentProject]: null,
            [GtmDimensions.ResourceGradeLevel]: null,
            [GtmDimensions.ResourceBrand]: null,
            [GtmDimensions.ResourceParentBrand]: null,
            [GtmDimensions.ResourceProjectType]: null,
            [GtmDimensions.ResourceOrganization]: null,
            [GtmDimensions.ResourceMediaType]: null,
            [GtmDimensions.ResourceCollection]: null,
            [GtmDimensions.ResourceSubcollection]: null,
            [GtmDimensions.ResourceId]: null,
        })
    }

    pushCleanPageView() {
        this.resetDataLayer()
        this.pushPageView()
    }

    setDataLayer(gtmData: GTMData) {
        const {
            resourceSubject,
            contentProject,
            resourceGradeLevel,
            resourceBrand,
            resourceParentBrand,
            resourceProjectType,
            resourceOrganization,
            resourceMediaType,
            resourceCollection,
            resourceSubcollection,
            resourceId,
            gtmObject,
        } = gtmData
        this.pushToDataLayer(
            {
                [GtmDimensions.ResourceSubject]: resourceSubject,
                [GtmDimensions.ContentProject]: contentProject,
                [GtmDimensions.ResourceGradeLevel]: resourceGradeLevel,
                [GtmDimensions.ResourceBrand]: resourceBrand,
                [GtmDimensions.ResourceParentBrand]: resourceParentBrand,
                [GtmDimensions.ResourceProjectType]: resourceProjectType,
                [GtmDimensions.ResourceOrganization]: resourceOrganization,
                [GtmDimensions.ResourceMediaType]: resourceMediaType,
                [GtmDimensions.ResourceCollection]: resourceCollection,
                [GtmDimensions.ResourceSubcollection]: resourceSubcollection,
                [GtmDimensions.ResourceId]: resourceId,
            },
            gtmObject
        )
    }

    pushSearchEvent(eventName, searchText) {
        // Should be used for all future search events (e.g. filters).
        this.pushToDataLayer({
            searchQuery: searchText,
        })
        this.pushEvent(eventName)
    }

    pushSearchResultClick(learningObject: LearningObject, searchResultsPage: number) {
        const tags = {
            [GtmDimensions.ClickResourceType]: learningObject.mediaType?.[0] || "",
            [GtmDimensions.ClickResourceCollection]:
                this.getResourceCollectionTitle(learningObject),
            [GtmDimensions.ClickResourceTitle]: learningObject.title,
            [GtmDimensions.PageOfSearchResults]: String(searchResultsPage),
        }
        this.pushToDataLayer(tags)
        this.pushEvent("search-result-click")
    }

    private getResourceCollectionTitle(learningObject: LearningObject): string {
        const collection = learningObject.isCollection
            ? learningObject.parentCollection
            : learningObject.primaryCollection
        return collection?.title || ""
    }

    pushToDataLayer(tags: TagData, resource?: Resource) {
        window.dataLayer.push(tags)
        this.subject.next({ tags, resource })
    }

    setVideoPlayerInfo(title?: string, contentProjectTitle?: string, guid?: string) {
        if (!title) {
            return
        }
        const parts = title.split(" | ")
        const payload: any = {
            [VideoPlayerInfo.VideoTitle]: parts[0]?.trim() || title,
            [VideoPlayerInfo.ShowTitle]: contentProjectTitle,
        }
        if (guid) {
            payload[VideoPlayerInfo.Guid] = guid
        }
        window.dataLayer.push(payload)
    }

    setVideoTimePlayed(videoInfo) {
        window.dataLayer.push({
            videoTimePlayed: videoInfo,
        })
    }

    setMediaType(type) {
        window.dataLayer.push({
            videoJsMediaType: type,
        })
    }

    subscribe(
        next?: (value: PageData) => void,
        error?: (error: any) => void,
        complete?: () => void
    ) {
        return this.subject.subscribe(next, error, complete)
    }
}
